import { useEffect, useRef, useState } from "react";
import { ActionIcon, Alert, Button, Group, Loader, Modal, SegmentedControl, Select, Stack, Text } from "@mantine/core";
import { IconArrowDown, IconArrowUp, IconCheck, IconGripVertical } from "@tabler/icons-react";
import { Reorder, useDragControls, useReducedMotion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { useGetMerchantServices } from "@/services/appointment/hooks/useGetMerchantServices";
import { orderCategories, orderServices } from "@/services/frontendFeatures";
import { moveItem, reorderByIds } from "./orderUtils";
import classes from "./orderEditor.module.scss";

type OrderRow = { id?: number; title?: string | null };

function SortableRow({ row, index, count, disabled, onStart, onEnd, onMove }: {
  row: OrderRow; index: number; count: number; disabled: boolean;
  onStart: () => void; onEnd: () => void; onMove: (offset: number) => void;
}) {
  const controls = useDragControls();
  const reducedMotion = useReducedMotion();
  return <Reorder.Item value={row.id} className={classes.row} dragListener={false}
    dragControls={controls} drag={disabled ? false : "y"} dragMomentum={false}
    onDragStart={onStart} onDragEnd={onEnd}
    transition={{ duration: reducedMotion ? 0 : 0.18 }}
    whileDrag={{ boxShadow: "0 8px 24px rgba(67, 43, 115, .16)", backgroundColor: "#f4efff" }}>
    <button type="button" className={classes.handle} disabled={disabled}
      aria-label={`جابه‌جایی ${row.title || "ردیف"}؛ با کلیدهای بالا و پایین`}
      onPointerDown={event => { if (!disabled) controls.start(event); }}
      onKeyDown={event => {
        if (event.key === "ArrowUp" || event.key === "ArrowDown") {
          event.preventDefault();
          if (!disabled) onMove(event.key === "ArrowUp" ? -1 : 1);
        }
      }}><IconGripVertical size={22} /></button>
    <span className={classes.position}>{(index + 1).toLocaleString("fa-IR")}</span>
    <Text className={classes.name} size="sm" fw={500}>{row.title || "بدون نام"}</Text>
    <Group gap={3} wrap="nowrap">
      <ActionIcon variant="subtle" color="gray" disabled={disabled || index === 0} aria-label={`انتقال ${row.title} به بالا`} onClick={() => onMove(-1)}><IconArrowUp size={16} /></ActionIcon>
      <ActionIcon variant="subtle" color="gray" disabled={disabled || index === count - 1} aria-label={`انتقال ${row.title} به پایین`} onClick={() => onMove(1)}><IconArrowDown size={16} /></ActionIcon>
    </Group>
  </Reorder.Item>;
}

export default function OrderEditor() {
  const [opened, setOpened] = useState(false);
  const [mode, setMode] = useState("categories");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [rows, setRows] = useState<OrderRow[]>([]);
  const rowsRef = useRef<OrderRow[]>([]);
  const locked = useRef(false);
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const { data, isLoading, isError, refetch } = useGetMerchantServices();
  const client = useQueryClient();
  const categoryId = mode === "services" ? Number(selectedCategory) : undefined;

  useEffect(() => {
    if (!opened || dragging || saving) return;
    const current = mode === "categories" ? data ?? [] : data?.find(c => String(c.id) === selectedCategory)?.services ?? [];
    rowsRef.current = current;
    setRows(current);
  }, [data, opened, mode, selectedCategory, dragging, saving]);

  const persist = async (ids: number[]) => {
    if (!data || locked.current) return;
    const previous = data;
    const original = categoryId === undefined ? data : data.find(c => c.id === categoryId)?.services ?? [];
    if (ids.every((id, index) => id === original[index]?.id) && ids.length === original.length) return;
    locked.current = true;
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const next = categoryId === undefined
        ? reorderByIds(data, ids)
        : data.map(category => category.id === categoryId ? { ...category, services: reorderByIds(category.services ?? [], ids) } : category);
      await client.cancelQueries({ queryKey: ["getMerchantServices"] });
      client.setQueryData(["getMerchantServices"], next);
      if (categoryId === undefined) await orderCategories(ids);
      else await orderServices(categoryId, ids);
      setSaved(true);
    } catch {
      client.setQueryData(["getMerchantServices"], previous);
      rowsRef.current = original;
      setRows(original);
      setError("ذخیره ترتیب انجام نشد؛ ترتیب قبلی بازگردانده شد. دوباره تلاش کنید.");
    } finally {
      await Promise.all(["getMerchantServices", "getMerchantUsers", "getMerchantUsersById"].map(key => client.invalidateQueries({ queryKey: [key] })));
      locked.current = false;
      setSaving(false);
    }
  };

  const move = (index: number, offset: number) => {
    if (locked.current || dragging) return;
    const next = moveItem(rowsRef.current, index, offset);
    rowsRef.current = next;
    setRows(next);
    void persist(next.map(row => row.id!));
  };
  const changeMode = (value: string) => {
    setMode(value);
    setSaved(false);
    setError("");
    if (!selectedCategory && data?.[0]?.id !== undefined) setSelectedCategory(String(data[0].id));
  };
  const invalidRows = rows.some(row => row.id === undefined);

  return <>
    <Button variant="light" leftSection={<IconGripVertical size={18} />} onClick={() => { setOpened(true); setSaved(false); setError(""); }}>ترتیب نمایش در سایت</Button>
    <Modal opened={opened} onClose={() => { if (!locked.current && !dragging) setOpened(false); }} title="ترتیب نمایش در سایت" size="lg" radius="lg">
      <Stack gap="md">
        <Text size="sm" c="dimmed">دستگیرهٔ کنار هر ردیف را بگیرید و بالا یا پایین بکشید. تغییرات پس از رها کردن، خودکار ذخیره می‌شوند.</Text>
        <SegmentedControl fullWidth value={mode} onChange={changeMode} disabled={saving || dragging} data={[{ value: "categories", label: "ترتیب دسته‌ها" }, { value: "services", label: "ترتیب خدمات" }]} />
        {mode === "services" && <Select label="خدمات کدام دسته؟" value={selectedCategory} onChange={value => { setSelectedCategory(value); setSaved(false); setError(""); }} disabled={saving || dragging} allowDeselect={false}
          data={(data ?? []).filter(category => category.id !== undefined).map(category => ({ value: String(category.id), label: category.title || "بدون نام" }))} />}
        {error && <Alert color="red">{error}</Alert>}
        {isError && <Alert color="red">دریافت خدمات انجام نشد.<Button variant="subtle" onClick={() => void refetch()}>تلاش دوباره</Button></Alert>}
        {invalidRows && <Alert color="red">شناسهٔ بعضی ردیف‌ها موجود نیست. فهرست را دوباره دریافت کنید.</Alert>}
        {isLoading ? <Loader size="sm" /> : !rows.length ? <Text ta="center" c="dimmed" py="xl">{mode === "categories" ? "دسته‌ای ثبت نشده است." : "این دسته خدمتی ندارد."}</Text> : <Reorder.Group axis="y" layoutScroll
          className={classes.list} values={rows.map(row => row.id)} aria-label={mode === "categories" ? "دسته‌ها به ترتیب نمایش در سایت" : "خدمات به ترتیب نمایش در سایت"}
          onReorder={(ids: number[]) => {
            if (locked.current || invalidRows) return;
            const next = reorderByIds(rowsRef.current, ids);
            rowsRef.current = next;
            setRows(next);
          }}>
          {rows.map((row, index) => <SortableRow key={row.id} row={row} index={index} count={rows.length}
            disabled={saving || invalidRows} onStart={() => { setDragging(true); setSaved(false); }}
            onEnd={() => { setDragging(false); void persist(rowsRef.current.map(item => item.id!)); }}
            onMove={offset => move(index, offset)} />)}
        </Reorder.Group>}
        <Group justify="space-between" className={classes.footer}>
          <div role="status" aria-live="polite"><Group gap={6}>
            {saving ? <><Loader size={14} /><Text size="xs" c="dimmed">در حال ذخیره ترتیب…</Text></> : saved ? <><IconCheck size={16} color="var(--mantine-color-green-6)" /><Text size="xs" c="green">ترتیب نمایش ذخیره شد</Text></> : <Text size="xs" c="dimmed">ردیف بالاتر، در سایت زودتر نمایش داده می‌شود.</Text>}
          </Group></div>
          <Button variant="default" disabled={saving || dragging} onClick={() => setOpened(false)}>تمام</Button>
        </Group>
      </Stack>
    </Modal>
  </>;
}
